import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildProviderAvailabilityBatchConfig } from "./configs";
import type { ProviderAvailabilityBatchParams } from "./configs/provider-availability-batch.config";
import { runManagedRequest } from "./request-manager";

export type {
  ProviderAvailabilityBatchParams,
  ProviderAvailabilityBatchRequestProvider,
} from "./configs/provider-availability-batch.config";

/**
 * Builds a stable identity for the complete batch payload. Provider IDs alone are
 * not sufficient: the same providers can be requested for different modalities,
 * practices, age ranges, or department scopes during filter transitions.
 */
function getProviderAvailabilityBatchRequestKey(params: ProviderAvailabilityBatchParams): string {
  const providers = params.providers
    .map((provider) => ({
      providerId: provider.providerId,
      clinicianType: provider.clinicianType,
      departmentIds: provider.departmentIds?.slice().sort((first, second) => first - second),
    }))
    .sort((first, second) => first.providerId.localeCompare(second.providerId));

  return `provider-availability-batch:${JSON.stringify({
    userUuid: params.user_uuid,
    contextId: params.context_id,
    modality: params.modality,
    userTimezone: params.user_timezone,
    ageRange: params.ageRange,
    dateOffset: params.dateOffset ?? 0,
    providers,
  })}`;
}

// biome-ignore lint/suspicious/noExplicitAny: provider-availability-batch passes through untyped API JSON
export async function getProviderAvailabilityBatch(
  params: ProviderAvailabilityBatchParams,
  callerSignal?: AbortSignal,
): Promise<any> {
  return runManagedRequest<any>({
    key: getProviderAvailabilityBatchRequestKey(params),
    // The same batch may be requested by consecutive renders. Share that request
    // rather than aborting it; a materially different payload receives its own key.
    policy: "dedupe-in-flight",
    callerSignal: callerSignal ?? new AbortController().signal,
    request: async (signal) => {
      const { data, response } = await callApi(
        buildProviderAvailabilityBatchConfig(params, signal),
      );

      if (!response.ok) {
        ClientLogsService.logError(
          params.user_uuid || "unknown",
          "provider-availability-batch.service.getProviderAvailabilityBatch",
          "Error fetching provider availability batch",
          new Error(`HTTP ${response.status}: ${response.statusText}`),
          {
            errorType: getFailureTypeForHttpStatus(response.status),
            dependencyName: "ProviderAvailabilityBatchAPI",
            functionName: "getProviderAvailabilityBatch",
          },
        );
        const err = data as { message?: string; transactionID?: string } | null;
        return {
          error: err?.message || "Failed to fetch provider availability batch",
          transactionID: err?.transactionID,
        };
      }
      return data;
    },
  });
}
